/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import useTable from 'components/Base/Table/hooks/useTable';
import { PlusOutlined } from '@ant-design/icons';
import { ETableStoragePaths } from 'components/Base/Table/Table.types';
import Button from 'components/Base/Button/Button';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import Spinner from 'components/Base/Spinner';
import {
  useLazyExportDataQuery,
  useGetActionPlansQuery,
} from 'redux/apiSlice/pillarsActionPlan.slice';
import { ESortDirection } from 'data/types/generic.types';
import { TTask } from 'data/types/pillarsActionPlan.types';
import { Flex } from 'antd';
import { EIconNames, Icon } from 'components/Base/Icon';
import EmptyTableState from 'components/Shared/EmptyTableState/EmptyTableState';
import PageErrorState from 'components/Shared/PageError/PageError';
import {
  AddTacticModal,
  EditTacticModal,
  DeleteTacticModal,
} from './TacticModals';
import { useGetColumnsData } from './TacticTable.data';
import { StyledTacticTable } from './TacticTable.style';
import { TTacticTask } from './TacticTable.types';
import TaskTable from './TaskTable/TaskTable';
import { ExpandButton } from './ActionButtons/ExpandButton';

interface TacticTableProps {
  isAllElcs?: boolean;
}

const TacticTable: React.FC<TacticTableProps> = ({ isAllElcs = false }) => {
  const { showToastNotification } = useShowToastNotification();

  const { id: elcId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tacticIdFromUrl = searchParams.get('tacticId');
  const actionPlanIdFromState = (location.state as any)?.actionPlanId;

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTactic, setSelectedTactic] = useState<TTacticTask | null>(
    null,
  );
  const [tasksByActionPlan, setTasksByActionPlan] = useState<
    Record<number, TTask[]>
  >({});
  const [isAddingTask, setIsAddingTask] = useState<boolean>(false);
  const isAddingTaskRef = React.useRef(isAddingTask);

  useEffect(() => {
    isAddingTaskRef.current = isAddingTask;
  }, [isAddingTask]);

  const updateTasksForActionPlan = useCallback(
    (actionPlanId: number, callback: (tasks: TTask[]) => TTask[]) => {
      setTasksByActionPlan(prev => ({
        ...prev,
        [actionPlanId]: callback(prev[actionPlanId] || []),
      }));
    },
    [],
  );

  const handleAddTask = useCallback(
    (tacticRecord: TTacticTask) => {
      if (isAddingTaskRef.current) {
        showToastNotification({
          type: ENotificationType.ERROR,
          message: 'Please complete the current task before adding a new one.',
        });
        return;
      }

      if (tacticRecord.isPreviousPlan) {
        showToastNotification({
          type: ENotificationType.ERROR,
          message:
            'This tactic is from a previous plan and cannot be modified.',
        });
        return;
      }

      setIsAddingTask(true);

      const rowId = `temp-${Date.now()}-${Math.random()}`;

      const newTask: TTask = {
        actionPlanId: tacticRecord.actionPlanId,
        rowId,
        taskTitle: '',
        status: 'TODO',
        dueDate: '',
        assignee: '',
      };

      updateTasksForActionPlan(tacticRecord.actionPlanId, tasks => [
        newTask,
        ...tasks,
      ]);
      setExpandedRowKeys([tacticRecord.actionPlanId]);
    },
    [updateTasksForActionPlan, isAddingTask],
  );

  const handleEditTactic = useCallback((tacticRecord: TTacticTask) => {
    setSelectedTactic(tacticRecord);
    setIsEditModalOpen(true);
  }, []);

  const handleDeleteTactic = useCallback((tacticRecord: TTacticTask) => {
    setSelectedTactic(tacticRecord);
    setIsDeleteModalOpen(true);
  }, []);

  // Build API query parameters with filters and sorting
  const apiParams = React.useMemo(() => {
    const params: any = {
      size: pageSize,
      page: currentPage - 1,
    };

    // Only include elcId if not viewing all ELCs
    if (!isAllElcs && elcId) {
      params.elcId = elcId;
    }

    if (tacticIdFromUrl) {
      params.tacticId = tacticIdFromUrl;
    }

    return params;
  }, [isAllElcs, elcId, pageSize, currentPage, tacticIdFromUrl]);

  const {
    data: actionPlansData,
    isLoading,
    error,
    refetch,
  } = useGetActionPlansQuery(apiParams);

  // Generate columns with API data when available
  const { defaultColumns } = useGetColumnsData(
    handleAddTask,
    handleEditTactic,
    handleDeleteTactic,
    actionPlansData,
    refetch,
    isAllElcs,
  );

  const {
    columns,
    handleTableChange,
    sortColumn,
    selectedFilters,
    onFiltersReset,
    setSelectedFilters,
    setSortColumn,
    setSelectedRows,
    setColumns,
    hasFilters,
  } = useTable<TTacticTask>({
    storagePath: ETableStoragePaths.TACTIC,
    defaultColumns,
  });

  // Reset all table filters when coming from "See on Action Plan" (when tacticId is in URL)
  React.useEffect(() => {
    if (tacticIdFromUrl) {
      onFiltersReset();
      setSelectedFilters({});
      setSortColumn({});
      setSelectedRows({
        selectedRowsKeys: [],
        selectedRowsRecords: [],
      });
    }
  }, [
    tacticIdFromUrl,
    onFiltersReset,
    setSelectedFilters,
    setSortColumn,
    setSelectedRows,
  ]);

  // Build updated API params with filters
  const finalApiParams = React.useMemo(() => {
    const params = { ...apiParams };

    // Add pillar filters
    if (selectedFilters?.pillarName && selectedFilters.pillarName.length > 0) {
      const pillarNameToIdMap: Record<string, number> = {
        Communities: 1,
        Services: 2,
        Neighborhoods: 3,
        Policies: 4,
      };

      params.pillars = (selectedFilters.pillarName as string[])
        .map((name: string) => pillarNameToIdMap[name])
        .filter((id: number) => id !== undefined)
        .map((id: number) => id.toString());
    }

    // Add tactic title filters - pass all selected titles
    if (
      selectedFilters?.tacticTitle &&
      selectedFilters.tacticTitle.length > 0
    ) {
      params.tacticTitles = selectedFilters.tacticTitle as string[];
    }

    if (
      selectedFilters?.tacticStatus &&
      (selectedFilters.tacticStatus as any[]).length > 0
    ) {
      params.statuses = selectedFilters.tacticStatus as string[];
    }

    // Add sorting parameters
    if (sortColumn.sortDirection) {
      params.sortDirection =
        sortColumn.sortDirection === ESortDirection.ascend ? 'ASC' : 'DESC';
    }
    if (sortColumn.sortField === 'completed') {
      params.sortField = 'dateCompleted';
    }

    return params;
  }, [apiParams, selectedFilters, sortColumn]);

  const {
    data: filteredActionPlansData,
    isLoading: isFilteredLoading,
    error: filteredError,
  } = useGetActionPlansQuery(finalApiParams);

  const activeData =
    Object.keys(selectedFilters || {}).length > 0 ||
    sortColumn.sortDirection ||
    sortColumn.sortField
      ? filteredActionPlansData
      : actionPlansData;
  const activeLoading =
    Object.keys(selectedFilters || {}).length > 0 ||
    sortColumn.sortDirection ||
    sortColumn.sortField
      ? isFilteredLoading
      : isLoading;
  const activeError =
    Object.keys(selectedFilters || {}).length > 0 ||
    sortColumn.sortDirection ||
    sortColumn.sortField
      ? filteredError
      : error;

  React.useEffect(() => {
    const dataWithFilters = filteredActionPlansData || actionPlansData;

    if (
      dataWithFilters?.filters?.tacticTitles &&
      dataWithFilters.filters.tacticTitles.length > 0
    ) {
      setColumns(prevColumns => {
        return prevColumns.map(column => {
          if (column.key === 'tacticTitle') {
            return {
              ...column,
              filters: dataWithFilters.filters.tacticTitles.map(title => ({
                text: title,
                value: title,
              })),
            };
          }
          return column;
        });
      });
    }
  }, [filteredActionPlansData, actionPlansData, setColumns]);

  const [triggerExportData] = useLazyExportDataQuery();

  const handleExport = async () => {
    try {
      if (!elcId) {
        showToastNotification({
          type: ENotificationType.ERROR,
          message: 'ELC ID is required to export data.',
        });

        return;
      }
      await triggerExportData({
        elcId,
        tacticId: finalApiParams.tacticId,
        pillars: finalApiParams.pillars,
        tacticTitles: finalApiParams.tacticTitles,
        statuses: finalApiParams.statuses,
        sortDirection: finalApiParams.sortDirection,
        sortField: finalApiParams.sortField,
      }).unwrap();

      showToastNotification({
        type: ENotificationType.SUCCESS,
        message: 'Data exported successfully.',
      });
    } catch (err) {
      showToastNotification({
        type: ENotificationType.ERROR,
        message: 'Failed to export data. Please try again.',
      });
    }
  };
  useEffect(() => {
    if (activeData && tacticIdFromUrl && activeData.page.number !== undefined) {
      const responsePageNumber = activeData.page.number + 1; // API is 0-indexed, UI is 1-indexed
      if (responsePageNumber !== currentPage) {
        setCurrentPage(responsePageNumber);
      }
    }
  }, [activeData, tacticIdFromUrl, currentPage]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    if (activeData && tacticIdFromUrl && activeData.page.content) {
      const targetTactic = activeData.page.content.find(
        item => item.tacticId.toString() === tacticIdFromUrl,
      );

      if (
        targetTactic &&
        !expandedRowKeys.includes(targetTactic.actionPlanId)
      ) {
        setExpandedRowKeys([targetTactic.actionPlanId]);

        timeoutId = setTimeout(() => {
          const rowElement = document.querySelector(
            `[data-row-key="${targetTactic.actionPlanId}"]`,
          );
          if (rowElement) {
            rowElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeData, tacticIdFromUrl, expandedRowKeys]);

  useEffect(() => {
    if (tacticIdFromUrl && activeData) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('tacticId');
      const newUrl = `${window.location.pathname}${
        newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''
      }`;
      navigate(newUrl, { replace: true });
    }
  }, [activeData, tacticIdFromUrl, searchParams, navigate]);

  // Handle auto-expansion when navigating from meeting action items
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | undefined;

    if (actionPlanIdFromState && activeData?.page.content) {
      const targetTactic = activeData.page.content.find(
        item => item.actionPlanId === actionPlanIdFromState,
      );

      if (
        targetTactic &&
        !expandedRowKeys.includes(targetTactic.actionPlanId)
      ) {
        setExpandedRowKeys([targetTactic.actionPlanId]);

        timeoutId = setTimeout(() => {
          const rowElement = document.querySelector(
            `[data-row-key="${targetTactic.actionPlanId}"]`,
          );
          if (rowElement) {
            rowElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        }, 100);
      }

      // Clear the navigation state after processing
      navigate(location.pathname, { replace: true, state: {} });
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    activeData,
    actionPlanIdFromState,
    expandedRowKeys,
    navigate,
    location.pathname,
  ]);

  const handleTaskComplete = useCallback(
    (actionPlanId: number, task: TTask) => {
      updateTasksForActionPlan(actionPlanId, tasks => [
        task,
        ...tasks.filter(t => t.taskId),
      ]);
      setIsAddingTask(false);
    },
    [updateTasksForActionPlan],
  );

  const handleTaskRemove = useCallback(
    (actionPlanId: number, rowId: string) => {
      updateTasksForActionPlan(actionPlanId, tasks =>
        tasks.filter(task => (task.rowId || task.taskId) !== rowId),
      );
      setIsAddingTask(false);
    },
    [updateTasksForActionPlan],
  );

  const expandedRowRender = useCallback(
    (record: TTacticTask) => {
      return (
        <TaskTable
          tasks={tasksByActionPlan[record.actionPlanId] || []}
          actionPlanId={record.actionPlanId}
          onTaskSaved={(_, newTask: TTask) =>
            handleTaskComplete(record.actionPlanId, newTask)
          }
          onTaskCancel={rowId => handleTaskRemove(record.actionPlanId, rowId)}
          onAddEmptyTask={() => handleAddTask(record)}
          isPreviousPlan={record.isPreviousPlan}
          isAllElcs={isAllElcs}
        />
      );
    },
    [tasksByActionPlan, handleTaskComplete, handleTaskRemove, isAllElcs],
  );

  // Modal handlers
  const handleAddTactic = async () => {
    try {
      setIsAddModalOpen(false);
      refetch();
    } catch (err) {
      console.error('Error in tactic addition callback:', err);
    }
  };

  const handleEditTacticSubmit = async () => {
    try {
      setIsEditModalOpen(false);
      setSelectedTactic(null);

      await refetch();
    } catch (err: any) {
      console.error('Error in tactic edit callback:', err);
      showToastNotification({
        type: ENotificationType.ERROR,
        message: 'Failed to refresh tactic data. Please refresh the page.',
      });
    }
  };

  const handleDeleteModalClose = () => {
    setIsDeleteModalOpen(false);
    setSelectedTactic(null);
    refetch();
  };

  // Handle pagination changes
  const handlePaginationChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
    }
  };

  // Show loading spinner while data is being fetched
  if (activeLoading) {
    return <Spinner size="large" />;
  }

  // Show error message if API call fails
  if (activeError) {
    return (
      <PageErrorState
        title="Failed to load action plans"
        message="There was an error loading the action plans. Please try again."
      />
    );
  }

  const tableData: TTacticTask[] =
    activeData?.page.content?.map(item => ({
      pillarId: item.pillarId,
      pillarName: item.pillarName,
      tacticTitle: item.tacticTitle,
      pillarOrder: item.pillarOrder,
      actionPlanId: item.actionPlanId,
      tacticId: item.tacticId,
      todoCount: item.todoCount,
      inProgressCount: item.inProgressCount,
      stalledCount: item.stalledCount,
      completedCount: item.completedCount,
      repeatOrder: item.repeatOrder,
      isCustomTactic: item.isCustomTactic,
      isPreviousPlan: item.isPreviousPlan,
      status: item.status as any,
      dateCompleted: item.dateCompleted as any,
      isGlobal: item.isGlobal,
      goalId: item.goalId ?? undefined,
      goalDescription: item.goalDescription ?? undefined,
      levelId: item.levelId ?? undefined,
      levelName: item.levelName ?? undefined,
    })) || [];

  return (
    <>
      <StyledTacticTable
        key={`${ETableStoragePaths.TACTIC}-${
          actionPlansData ? 'loaded' : 'loading'
        }`}
        storagePath={ETableStoragePaths.TACTIC}
        data-cy="task_list_table"
        tableLayout="fixed"
        scroll={{ x: 1440 }}
        pageHeaderContent={50}
        hasFilters={hasFilters}
        onFiltersReset={onFiltersReset}
        additionalActions={
          <Flex gap={6}>
            {!isAllElcs && (
              <Button
                variant="default"
                onClick={handleExport}
                data-cy="export_tasks_btn"
                icon={
                  <Icon
                    icon={EIconNames.DOWNLOAD}
                    alt="filter icon"
                    color="inherit"
                  />
                }
                style={{ fontWeight: 600 }}
              >
                Export
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => setIsAddModalOpen(true)}
              data-cy="add_task_btn"
            >
              <PlusOutlined />
              Add Tactic
            </Button>
          </Flex>
        }
        tableTitle="Tactic Tasks"
        columns={columns as any[] | undefined}
        dataSource={tableData.map(item => ({
          ...item,
          key: item.actionPlanId,
        }))}
        expandable={
          isAllElcs
            ? undefined
            : {
                expandedRowRender,
                onExpand: (expanded, record) => {
                  if (isAddingTaskRef.current) return;
                  setExpandedRowKeys(expanded ? [record.actionPlanId] : []);
                },
                expandIcon: props => (
                  <ExpandButton {...props} disabled={isAddingTask} />
                ),
                expandedRowKeys,
              }
        }
        pagination={{
          current: currentPage,
          pageSize,
          total: activeData?.page.totalElements || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          onChange: handlePaginationChange,
          onShowSizeChange: handlePaginationChange,
          showTotal: (total, range) =>
            `Results: ${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={handleTableChange}
        rowKey="key"
        className="expandable-table"
        locale={{
          emptyText: () => (
            <EmptyTableState
              message="You do not have tactics yet. To start your action plan, visit the
        Pillars of Strength and choose a tactic"
            />
          ),
        }}
      />

      <AddTacticModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddTactic}
        elcId={elcId}
      />

      {selectedTactic && (
        <EditTacticModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedTactic(null);
          }}
          onEdit={handleEditTacticSubmit}
          tactic={selectedTactic}
          elcId={elcId}
        />
      )}

      <DeleteTacticModal
        open={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        tactic={selectedTactic}
      />
    </>
  );
};

export default TacticTable;
